/* Los tipos de licencia, con nombre.
 *
 * ── POR QUÉ EXISTE ───────────────────────────────────────────────────────────
 *
 * La pantalla de Licencias sabía dos tipos —enfermo y vacaciones— y todo lo
 * demás lo dibujaba con un ternario:
 *
 *     {l.tipo === "enfermo" ? "🤒 enfermo" : "🏖️ vacaciones"}
 *
 * Eso estuvo bien mientras la tabla tuvo dos tipos. Dejó de estarlo cuando
 * entró el histórico de ausentismo: llegaron a ser DIECISIETE (hoy, con los
 * que pidió Personal, son veintiocho), y una suspensión, una
 * ART o un ausente sin aviso se estaban leyendo en pantalla como
 * «🏖️ vacaciones». No es un rótulo feo — es un rótulo FALSO, y del tipo que
 * nadie sale a verificar porque parece un dato normal.
 *
 * ── LA OTRA COPIA, Y POR QUÉ SE BANCA ────────────────────────────────────────
 *
 * El gemelo de esta tabla es `licencias.py` (`ETIQUETA` y `EMOJI`) del lado
 * del bot, que es de donde salen los rótulos de WhatsApp y del patio. Son dos
 * aplicaciones en dos lenguajes: no hay import posible, así que hay dos
 * copias y esto se dice en voz alta en los dos lados. **Si acá se agrega un
 * tipo, va también allá.**
 *
 * ── EL TIPO QUE NO CONOZCO SE MUESTRA ────────────────────────────────────────
 *
 * `rotuloDeLicencia` de un tipo que no está en la tabla devuelve el tipo
 * CRUDO, no «Licencia» ni un vacío. Que se vea feo es el punto: un tipo nuevo
 * tiene que pedir que alguien lo ubique, no esconderse detrás de una etiqueta
 * genérica que se lee como si alguien la hubiera elegido.
 */

/* La zona ordena el color, y es el mismo vocabulario que usa el patio del bot
 * para decidir a quién manda a la enfermería, a la laguna y afuera del
 * portón. Que las dos pantallas agrupen igual no es cosmético: es lo que hace
 * que «los de salud» quiera decir lo mismo en las dos. */
export const ZONAS = { SALUD: "salud", DESCANSO: "descanso", AUSENCIA: "ausencia" };

export const TIPOS_DE_LICENCIA = {
  enfermo: { label: "Parte de enfermo", emoji: "🤒", zona: ZONAS.SALUD },
  art: { label: "ART", emoji: "🩹", zona: ZONAS.SALUD },
  accidente: { label: "Accidente", emoji: "🩹", zona: ZONAS.SALUD },
  covid: { label: "Covid", emoji: "🤒", zona: ZONAS.SALUD },
  no_puede_manejar: {
    label: "No puede manejar",
    emoji: "🤒",
    zona: ZONAS.SALUD,
  },

  vacaciones: { label: "Vacaciones", emoji: "🏖️", zona: ZONAS.DESCANSO },
  franco: { label: "Franco", emoji: "🗓️", zona: ZONAS.DESCANSO },

  ausente_con_aviso: {
    label: "Ausente con aviso",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  ausente_sin_aviso: {
    label: "Ausente sin aviso",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  ausente: { label: "Ausente", emoji: "📋", zona: ZONAS.AUSENCIA },
  suspension: { label: "Suspensión", emoji: "📋", zona: ZONAS.AUSENCIA },
  examen: { label: "Licencia por examen", emoji: "📋", zona: ZONAS.AUSENCIA },
  licencia_sin_goce: {
    label: "Licencia sin goce de sueldo",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  licencia_especial: {
    label: "Licencia especial",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  llegada_tarde: { label: "Llegada tarde", emoji: "📋", zona: ZONAS.AUSENCIA },
  baja: { label: "Baja", emoji: "📋", zona: ZONAS.AUSENCIA },
  sin_tipo: { label: "Sin tipo", emoji: "📋", zona: ZONAS.AUSENCIA },

  /* Los que pidió Personal (Vilte y Erick, 08/10/2026), con el nombre que
   * usan ellos. Erick: «todas estas deben estar con Inasistencias». */
  falta_injustificada: {
    label: "Falta injustificada",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  licencia_con_goce: {
    label: "Licencia con goce de sueldo",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  licencia_gremial: {
    label: "Licencia gremial con goce",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  permiso_especial: {
    label: "Permiso especial",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  reserva_de_puesto: {
    label: "Reserva de puesto",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  mudanza: { label: "Licencia por mudanza", emoji: "📦", zona: ZONAS.AUSENCIA },
  maternidad: {
    label: "Licencia por maternidad",
    emoji: "👶",
    zona: ZONAS.AUSENCIA,
  },
  paternidad: {
    label: "Licencia por paternidad",
    emoji: "👶",
    zona: ZONAS.AUSENCIA,
  },
  matrimonio: {
    label: "Licencia por matrimonio",
    emoji: "💍",
    zona: ZONAS.AUSENCIA,
  },
  fallecimiento: {
    label: "Fallecimiento de familiar",
    emoji: "📋",
    zona: ZONAS.AUSENCIA,
  },
  donacion_sangre: {
    label: "Donación de sangre",
    emoji: "🩸",
    zona: ZONAS.AUSENCIA,
  },
};

/* Los que se pueden CARGAR desde el formulario, agrupados como los piensa
 * Personal. Siguen siendo menos que los que se pueden leer: `accidente`,
 * `covid`, `ausente` a secas, `ausente_sin_aviso`, `licencia_especial`,
 * `no_puede_manejar`, `llegada_tarde`, `baja` y `sin_tipo` entraron con el
 * histórico de ausentismo y Personal no los pidió para cargar.
 *
 * `franco` está acá porque sin él el banco de francos del bot no tiene de
 * dónde restar: mide lo que se gana contra lo que se toma, y lo tomado sólo
 * existe si alguien lo carga.
 *
 * Las inasistencias van por orden alfabético: son quince, y en un
 * desplegable largo se busca leyendo, no recordando en qué orden las dictó
 * alguien. */
const porNombre = (a, b) =>
  TIPOS_DE_LICENCIA[a].label.localeCompare(TIPOS_DE_LICENCIA[b].label, "es");

export const GRUPOS_QUE_SE_CARGAN = [
  { titulo: "Salud", tipos: ["enfermo", "art"] },
  { titulo: "Descanso", tipos: ["vacaciones", "franco"] },
  {
    titulo: "Inasistencias",
    tipos: [
      "ausente_con_aviso",
      "donacion_sangre",
      "falta_injustificada",
      "fallecimiento",
      "licencia_con_goce",
      "licencia_gremial",
      "licencia_sin_goce",
      "examen",
      "matrimonio",
      "maternidad",
      "mudanza",
      "paternidad",
      "permiso_especial",
      "reserva_de_puesto",
      "suspension",
    ].sort(porNombre),
  },
];

export const TIPOS_QUE_SE_CARGAN = GRUPOS_QUE_SE_CARGAN.flatMap((g) => g.tipos);

/* `{ label, emoji, zona }` de un tipo. El que no conozco vuelve CRUDO. */
export function rotuloDeLicencia(tipo) {
  const t = String(tipo ?? "").trim();
  return (
    TIPOS_DE_LICENCIA[t] || {
      label: t || "sin tipo",
      emoji: "📋",
      zona: ZONAS.AUSENCIA,
      desconocido: true,
    }
  );
}

/* La clase de la pastilla. Vigente manda: una licencia que ya terminó es
 * historia y va en gris, sea del tipo que sea. */
export function pastillaDeLicencia(tipo, { vigente = true, anulada = false } = {}) {
  if (anulada || !vigente) return "gris";
  const { zona } = rotuloDeLicencia(tipo);
  if (zona === ZONAS.SALUD) return "mal";
  if (zona === ZONAS.DESCANSO) return "ok";
  return "ojo";
}
